import { ICompileService } from "angular";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../records/forms/formly/fieldConfigurationHelpers";
import { FormSubmitArgs } from "../../../records/forms/formView/controller";
import {
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
} from "../../../records/forms/formView/provider";
import {
  FormViewConfig,
  FormViewService,
  vsServiceName as FormViewServiceName,
} from "../../../records/forms/formView/service";
import { RecordDataSource } from "../../../records/grids";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import { FilterProvider } from "../../../records/grids/columns/filters/FilterProvider";
import { LookupFilter } from "../../../records/grids/columns/filters/subFilters/LookupFilter";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import {
  GridConfiguration,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../../records/views/recordGridView";
import fieldHelpers from "../../../ui-formly/fieldHelpers";
import { vsLookupManagementServiceProviderName as LookupManagementServiceProviderName } from "../../shared/lookups";
import {
  LookupManagementConfig,
  LookupManagementServiceProvider,
} from "../../../records/lookup/lookupManagement";
import { MachineBank, MachineBankView } from "../models/machineConfiguration";

export const vsObjectName = "machineBank";

const url = "/api/machines/MachineBank";

MachineBankConfig.$inject = [
  FormViewServiceProviderName,
  RecordGridViewServiceProviderName,
  LookupManagementServiceProviderName,
];

export function MachineBankConfig(
  formViewServiceProvider: FormViewServiceProvider,
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  lookupManagementServiceProvider: LookupManagementServiceProvider
) {
  recordGridViewServiceProvider.addGridConfiguration(
    vsObjectName,
    getGridConfig
  );
  lookupManagementServiceProvider.addLookup(vsObjectName, {
    displayName: "Bank",
    config: [
      ServerUtilityName,
      (serverUtility: ServerUtility) => {
        const config: LookupManagementConfig = {
          fetchFormModel(id) {
            return serverUtility.querySingle({
              url,
              query: { Id: id },
            });
          },
          baseUrl: url,
          getLongItemDisplay: (item: MachineBankView) =>
            `${item.PropertyName}-${item.MachineAreaName}-${item.MachineZoneName}-${item.Name}`,
        };
        return config;
      },
    ],
  });
  formViewServiceProvider.addFormViewConfig(vsObjectName, getFormConfig);
}

getGridConfig.$inject = [
  ColumnDefinitionServiceName,
  "$compile",
  ServerUtilityName,
];
function getGridConfig(
  columnDefinitionService: ColumnDefinitionService,
  $compile: ICompileService,
  serverUtility: ServerUtility
): GridConfiguration {
  const config: GridConfiguration = {
    columns: [
      columnDefinitionService.getNumberColDef({ field: "Id", hide: true }),
      columnDefinitionService.getStringColDef({
        field: "PropertyName",
        header: "Property",
        filter: new FilterProvider("PropertyName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["PropertyId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: "/api/properties",
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineAreaName",
        header: "Area",
        filter: new FilterProvider("MachineAreaName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineAreaId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: "/api/machines/MachineArea",
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineZoneName",
        header: "Zone",
        filter: new FilterProvider("MachineZoneName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineZoneId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: "/api/machines/MachineZone",
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "Name",
        header: "Bank",
        filter: new FilterProvider("Name", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getBooleanColDef({
        field: "Disabled",
      }),
    ],
    gridDataSource: new RecordDataSource(serverUtility, url),
    defaultSort: "PropertyName,MachineAreaName,MachineZoneName,Name",
    namePlural: "Banks",
    nameSingular: "Bank",
    showAddButton: true,
    showAddButtonPermission: "machineLookupModify",
    showSelectButtons: false,
  };
  return config;
}

getFormConfig.$inject = [
  FieldConfigurationHelperServiceName,
  FormViewServiceName,
];
function getFormConfig(
  fieldConfigurationHelperService: FieldConfigurationHelperService,
  formViewService: FormViewService
): FormViewConfig {
  return {
    fields: [
      fieldConfigurationHelperService.selectAsyncField({
        key: "PropertyId",
        name: "PropertyId",
        label: "Property",
        required: true,
        getDefaultItem: (model) => {
          return { Id: model.PropertyId, Name: model.PropertyName };
        },
        queryUrl: "/api/properties",
      }),
      fieldConfigurationHelperService.selectAsyncField({
        key: "MachineAreaId",
        name: "MachineAreaId",
        label: "Area",
        required: true,
        disableDefaultQueryCaching: true,
        getDefaultItem: (model) => {
          return { Id: model.MachineAreaId, Name: model.MachineAreaName };
        },
        expressionProperties: {
          "templateOptions.readonly": (_, __, scope) =>
            scope.model?.PropertyId === undefined,
        },
        watcher: [
          {
            expression: "model.PropertyId",
            listener: (_, newValue, oldValue, scope) => {
              if (scope.model && newValue !== oldValue) {
                scope.model.MachineAreaId = undefined;
              }
            },
          },
        ],
        queryBuilder: (search, model) => {
          return {
            PropertyId: model.PropertyId,
            NameContains: search,
          };
        },
        queryUrl: "/api/machines/MachineArea",
      }),
      fieldConfigurationHelperService.selectAsyncField({
        key: "MachineZoneId",
        name: "MachineZoneId",
        label: "Zone",
        required: true,
        disableDefaultQueryCaching: true,
        getDefaultItem: (model) => {
          return { Id: model.MachineZoneId, Name: model.MachineZoneName };
        },
        queryBuilder: (search, model) => {
          return {
            MachineAreaId: model.MachineAreaId,
            NameContains: search,
          };
        },
        expressionProperties: {
          "templateOptions.readonly": (_, __, scope) =>
            scope.model?.MachineAreaId === undefined,
        },
        watcher: [
          {
            expression: "model.MachineAreaId",
            listener: (_, newValue, oldValue, scope) => {
              if (scope.model && newValue !== oldValue) {
                scope.model.MachineZoneId = undefined;
              }
            },
          },
        ],
        queryUrl: "/api/machines/MachineZone",
      }),
      fieldHelpers.simpleStringField({
        key: "Name",
        label: "Bank",
        required: true,
      }),
      fieldHelpers.simpleBoolField({
        key: "Disabled",
        defaultValue: false,
      }),
    ],
    title: (model) => (model.Id ? "Edit Bank" : "Create Bank"),
    name: vsObjectName,
    submitForm: (args: FormSubmitArgs) => {
      const model = <MachineBank>args.model;
      return formViewService.saveForm({
        formObject: model,
        formViewController: args.formViewController,
        url,
      });
    },
  };
}
